import { useTranslation } from 'react-i18next';
import { Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { audienceLabel } from '../lib/base-garments-audience';
import { WHO_ANY, parseWhoParam, serializeWho, type WhoCanUseFilter as WhoFilter } from '../lib/base-garments-url';

const ANY_VALUE = 'any';

export interface WhoCanUseFilterProps {
  value: WhoFilter;
  onChange: (next: WhoFilter) => void;
  /** The org's connected clients (every floor it owns), by name. */
  clients: ReadonlyArray<{ id: string; name: string }>;
  disabled?: boolean;
  className?: string;
}

/**
 * "Stock type: Any / Own stock / Shared / Labelled for <client>" (F3, R7).
 * A server query input, never a filter on the page in hand (filter, count and
 * page at one layer). Shown only to a 3PL org: an org with no clients has
 * nothing to tell apart. Dashed while it narrows nothing, solid purple while it
 * does, with a clear button beside it.
 *
 * Reusable on Products and Inventory (F25): the caller owns the state.
 */
export function WhoCanUseFilter({ value, onChange, clients, disabled = false, className }: WhoCanUseFilterProps) {
  const { t } = useTranslation('inventory');
  const active = value.kind !== 'any';
  const selected = serializeWho(value) ?? ANY_VALUE;
  // A client picked before the roster loads stays listed (as "Labelled for a
  // client" until its name arrives), so the trigger never blanks.
  const listedClients =
    value.kind === 'client' && !clients.some((client) => client.id === value.clientOrganizationId)
      ? [...clients, { id: value.clientOrganizationId, name: '' }]
      : clients;

  const label =
    value.kind === 'any'
      ? t('baseGarments.who.anyone')
      : audienceLabel(
          t,
          value.kind,
          value.kind === 'client'
            ? (listedClients.find((client) => client.id === value.clientOrganizationId)?.name ?? null)
            : null,
        );

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Select
        value={selected}
        onValueChange={(next) => onChange(next === ANY_VALUE ? WHO_ANY : parseWhoParam(next))}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            'h-8 w-auto min-w-[200px] max-w-[18rem] gap-1.5 text-xs transition-colors duration-150 motion-reduce:transition-none',
            active
              ? 'border-threadheads-purple text-foreground'
              : 'border-dashed text-muted-foreground',
          )}
          aria-label={t('filters.whoCanUse')}
        >
          <Users className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="shrink-0">{t('filters.whoCanUse')}:</span>
          <SelectValue>
            <span className={cn('block truncate', active && 'font-semibold')} title={label}>
              {label}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY_VALUE} className="text-xs">
            {t('baseGarments.who.anyone')}
          </SelectItem>
          <SelectItem value="own" className="text-xs">
            {audienceLabel(t, 'own', null)}
          </SelectItem>
          <SelectItem value="all" className="text-xs">
            {audienceLabel(t, 'all_clients', null)}
          </SelectItem>
          {listedClients.length > 0 && (
            <>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('baseGarments.who.reservedFor')}
                </SelectLabel>
                {listedClients.map((client) => (
                  <SelectItem key={client.id} value={client.id} className="text-xs">
                    {audienceLabel(t, 'client', client.name)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </>
          )}
        </SelectContent>
      </Select>
      {active && (
        <Button
          type="button"
          variant="ghost"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          onClick={() => onChange(WHO_ANY)}
          aria-label={t('baseGarments.filter.clearWho')}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}
